import React, { useEffect, useRef } from 'react';

export interface IconProps extends React.SVGProps<SVGSVGElement> {
  /**
   * SVG component generated by SVGR.
   */
  src: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * Accessible label for meaningful icons. When not provided, icon is marked as decorative.
   */
  ariaLabel?: string;
}

const Icon: React.FC<IconProps> = ({ src: IconComponent, ariaLabel, ...rest }) => {
  const ref = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && ariaLabel) {
      const interactiveParent = ref.current?.closest(
        'button, a, [role="button"], [role="link"], input[type="button"], input[type="submit"]',
      ) as HTMLElement | null;

      if (
        interactiveParent &&
        !interactiveParent.textContent?.trim() &&
        (interactiveParent.getAttribute('aria-label') ||
          interactiveParent.getAttribute('aria-labelledby'))
      ) {
        console.warn(
          'Icon: avoid providing ariaLabel when the parent interactive element already has an accessible name.',
        );
      }
    }
  }, [ariaLabel]);

  const accessibleProps = ariaLabel
    ? { 'aria-label': ariaLabel, role: 'img' }
    : { 'aria-hidden': true };

  return <IconComponent ref={ref} {...accessibleProps} {...rest} />;
};

export default Icon;
